import {createApi} from '@reduxjs/toolkit/query/react';
import {customAxiosBaseQuery} from '../API/customAxiosBaseQuery';

import {BaseResponseType} from '../types/TypesStore';
import {
	ApproveBonusSmsCode,
	MoveDataType,
	PresentClientType,
	ResponseClientCardType,
	ResponseSearchClientType,
	ResponsQLFCType,
	UserActivity,
	UserClient,
} from '../types/TypesUser';
import {clientsAPI} from '../API/api';
import {
	RequestTeamType,
	ResponseClientStatType,
	ResponseMyTeamHeaderType,
	ResponseMyTeamType,
} from '../types/TypesPage';
import {
	BonusesType,
	ListAllLevelsType,
	UserInRegListType,
} from '../types/TypesUserProfit';

export const userClientsApi = createApi({
	reducerPath: 'userClientsApi',
	baseQuery: customAxiosBaseQuery,
	tagTypes: [
		'clientsTree',
		'searched-client',
		'my-team',
		'my-team-header',
		'list-levels-all',
		'QlfcLevel',
		'activityTree',
		'my-team-client-order',
		'my-team-client-stat',
		'clients-regList',
	],
	endpoints: (builder) => ({
		searchClientByContract: builder.query<
			ResponseSearchClientType,
			{month: string; contract: string}
		>({
			query: (searchData) => ({
				func: clientsAPI.searchClientByContract,
				args: [searchData],
			}),
			providesTags: ['searched-client'],
		}),
		getUserClientsTree: builder.query<
			BaseResponseType & {
				children: UserClient[];
			},
			string
		>({
			query: (month) => ({
				func: clientsAPI.getClientsTree,
				args: [month],
			}),
			providesTags: ['clientsTree'],
		}),
		getUserActivityTree: builder.query<
			BaseResponseType & {
				activity: UserActivity[];
			},
			string
		>({
			query: (month) => ({
				func: clientsAPI.getActivityTree,
				args: [month],
			}),
			providesTags: ['activityTree'],
		}),
		getQlfcLevel: builder.query<BaseResponseType & ResponsQLFCType, string>({
			query: (month) => ({
				func: clientsAPI.getQlfcLevel,
				args: [month],
			}),
			providesTags: ['QlfcLevel'],
		}),
		getClientsPresent: builder.query<
			BaseResponseType & {
				present: PresentClientType[];
			},
			number
		>({
			query: (client_id) => ({
				func: clientsAPI.getClientsPresent,
				args: [client_id],
			}),
			providesTags: ['QlfcLevel'],
		}),
		getMyTeam: builder.query<ResponseMyTeamType, RequestTeamType>({
			query: (props?) => ({
				func: clientsAPI.getMyTeam,
				args: [props],
			}),
			providesTags: ['my-team'],
		}),
		getMyTeamClientOrder: builder.query<
			ResponseMyTeamType,
			{month: string; client_id: number}
		>({
			query: (data) => ({
				func: clientsAPI.getMyTeamClientOrder,
				args: [data],
			}),
			providesTags: ['my-team-client-order'],
		}),
		getUserClientStat: builder.query<
			ResponseClientStatType,
			{month: string; client_id: number}
		>({
			query: (data) => ({
				func: clientsAPI.getUserClientStat,
				args: [data],
			}),
			providesTags: ['my-team-client-stat'],
		}),
		getMyTeamHeader: builder.query<ResponseMyTeamHeaderType, string>({
			query: (month) => ({
				func: clientsAPI.getMyTeamHeader,
				args: [month],
			}),
			providesTags: ['my-team-header'],
		}),
		sendBonusSms: builder.mutation<BaseResponseType, number>({
			query: (client_id) => ({
				func: clientsAPI.sendBonusSms,
				args: [client_id],
			}),
		}),
		clientInfo: builder.query<ResponseClientCardType, number>({
			query: (client_id) => ({
				func: clientsAPI.getClientInfo,
				args: [client_id],
			}),
		}),
		approveBonusSms: builder.mutation<BaseResponseType, ApproveBonusSmsCode>({
			query: (data) => ({
				func: clientsAPI.approveBonusCodeSms,
				args: [data],
			}),
		}),
		getProfitMonth: builder.query<BaseResponseType & BonusesType, string>({
			query: (month) => ({
				func: clientsAPI.getProfitMonth,
				args: [month],
			}),
			providesTags: ['list-levels-all'],
		}),
		getListAllLevels: builder.query<
			BaseResponseType & ListAllLevelsType,
			string
		>({
			query: (month) => ({
				func: clientsAPI.getListAllLevels,
				args: [month],
			}),
			providesTags: ['list-levels-all'],
		}),
		getClientsRegList: builder.query<
			BaseResponseType & {users: UserInRegListType[]},
			string
		>({
			query: (month) => ({
				func: clientsAPI.getClientsRegList,
				args: [month],
			}),
			providesTags: ['clients-regList'],
		}),
		getClientsRegMove: builder.mutation<BaseResponseType, MoveDataType>({
			query: (moveData) => ({
				func: clientsAPI.getClientsRegMove,
				args: [moveData],
			}),
			invalidatesTags: ['clients-regList'],
		}),
	}),
});

export const {
	useGetUserClientsTreeQuery,
	useGetUserActivityTreeQuery,
	useLazySearchClientByContractQuery,
	useSendBonusSmsMutation,
	useApproveBonusSmsMutation,
	useGetMyTeamQuery,
	useGetMyTeamHeaderQuery,
	useClientInfoQuery,
	useGetListAllLevelsQuery,
	useGetProfitMonthQuery,
	useGetQlfcLevelQuery,
	useGetMyTeamClientOrderQuery,
	useGetClientsPresentQuery,
	useGetUserClientStatQuery,
	useGetClientsRegListQuery,
	useGetClientsRegMoveMutation,
} = userClientsApi;
